// Referral tracking utilities for affiliate system

const COOKIE_NAME = "sba_ref";
const STORAGE_KEY = "sba_referral";
const COOKIE_TTL_DAYS = 30;

export interface ReferralData {
  code: string;
  timestamp: number;
  landingPage: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Sets the referral cookie with affiliate code and tracking data
 */
export function setReferralCookie(data: ReferralData): void {
  try {
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_TTL_DAYS);

    const cookieValue = encodeURIComponent(JSON.stringify(data));
    document.cookie = `${COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    // Also store in localStorage as backup
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error setting referral cookie:", error);
  }
}

/**
 * Gets the referral data from cookie or localStorage
 */
export function getReferralData(): ReferralData | null {
  try {
    // Try cookie first
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === COOKIE_NAME && value) {
        const data = JSON.parse(decodeURIComponent(value)) as ReferralData;
        // Check if still valid (within TTL)
        const expiryTime = data.timestamp + COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000;
        if (Date.now() < expiryTime) {
          return data;
        }
      }
    }

    // Fall back to localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored) as ReferralData;
      const expiryTime = data.timestamp + COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000;
      if (Date.now() < expiryTime) {
        return data;
      }
      // Expired, clean up
      localStorage.removeItem(STORAGE_KEY);
    }

    return null;
  } catch (error) {
    console.error("Error getting referral data:", error);
    return null;
  }
}

/**
 * Clears the referral cookie and localStorage
 */
export function clearReferralData(): void {
  try {
    // Clear cookie
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing referral data:", error);
  }
}

/**
 * Parses URL parameters for referral data
 * Supports: ?ref=CODE or UTM parameters
 */
export function parseReferralFromUrl(): Partial<ReferralData> | null {
  try {
    const params = new URLSearchParams(window.location.search);

    // Check for direct referral code
    const refCode = params.get("ref") || params.get("affiliate");

    // Check for UTM parameters
    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");

    // If we have a ref code, use it
    if (refCode) {
      return {
        code: refCode.toUpperCase(),
        utmSource: utmSource || undefined,
        utmMedium: utmMedium || undefined,
        utmCampaign: utmCampaign || undefined,
      };
    }

    // If UTM source looks like an affiliate code, use it
    if (utmSource && utmMedium === "affiliate") {
      return {
        code: utmSource.toUpperCase(),
        utmSource,
        utmMedium,
        utmCampaign: utmCampaign || undefined,
      };
    }

    return null;
  } catch (error) {
    console.error("Error parsing referral from URL:", error);
    return null;
  }
}

/**
 * Generates an affiliate referral link
 */
export function generateReferralLink(affiliateCode: string, baseUrl?: string): string {
  const base = baseUrl || window.location.origin;
  return `${base}?ref=${affiliateCode}`;
}

/**
 * Generates an affiliate referral link for a specific product
 */
export function generateProductReferralLink(
  affiliateCode: string,
  productSlug: string,
  baseUrl?: string
): string {
  const base = (baseUrl || window.location.origin).replace(/\/+$/, "");
  return `${base}/products/${productSlug}?ref=${affiliateCode}`;
}

/**
 * Formats cents to dollar string
 */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Formats a percentage (0.10 -> "10%")
 */
export function formatPercentage(rate: number): string {
  return `${(rate * 100).toFixed(0)}%`;
}
